import {
  BilletoEventOverviewType,
  BilletoEventTicketType,
  BillettoEventType,
  mapBillettoEventToLinkListData,
} from "@/getData/fetchBillettoData";
import { Section } from "../Section/Section";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import styles from "./EventSingleView.module.scss";
import { CustomSVG } from "../_primitives/CustomSVG/CustomSVG";
import {
  calculateFormatedDuration,
  getFormatedDatetimeSpan,
} from "@/utils/datetime";
import { useRouter } from "next/router";
import { ReactNode } from "react";
import { Button } from "../Button/Button";
import LinkListModule, {
  LinkListModuleType,
} from "@/modules/LinkListModule/LinkListModule";
import { LinkListOptionsType } from "@/types/ModuleOptionsType";

type EventSingleViewProps = {
  billettoEvent: BillettoEventType;
  billettoMoreEvents: BilletoEventOverviewType[];
  linkListOptions: LinkListOptionsType | null;
};

export const EventSingleView = ({
  billettoEvent,
  billettoMoreEvents,
  linkListOptions,
}: EventSingleViewProps) => {
  const { getLabel, getIcon } = useGlobalContext();
  const { locale } = useRouter();

  const {
    id,
    starts_at,
    ends_at,
    public_url,
    // location: { name, address_line_1, postal_code, city },
    location,
    ticket_types: { data },
  } = billettoEvent;
  const { name, address_line_1, postal_code, city } = location ? location : {};
  const getAllTicketOptions = (data: BilletoEventTicketType[]) => {
    const priceFormatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "EUR",
    });

    return (
      <section>
        {data.map((ticket) => (
          <p key={`ticket-${ticket.id}`}>
            {`${ticket.name}: ${priceFormatter.format(ticket.price / 100)}`}
          </p>
        ))}
      </section>
    );
  };

  const mappedElements = mapBillettoEventToLinkListData(billettoMoreEvents, {
    icon: getIcon("liEventNobleBC"),
    ariaLabel: getLabel("aria_label_billetto_event_icon"),
    linkTitle: getLabel("link_title_billetto_single_event"),
  });

  const moreEventsData: LinkListModuleType = {
    linklist: {
      headline: getLabel("event_single_view_more_events_headline"),
      headline_html_tag: "h2",
      overline: "",
      subline: "",
      type: "manual",
      location: false,
      jobs: null,
      insights: null,
      press: null,
      events: null,
      team: null,
      elements: mappedElements,
      link: null,
    },
    layout: {
      id: "more-events-section",
      background_color_class: "has-hellgrau-bg",
      section_margin_class: "is-gapless-margin-top-bottom",
      section_padding_class: "default",
      module_width: "narrow-frame",
      headline_text_align_class: "has-text-left",
      text_align_class: "has-text-left",
      toggle_cardbox: true,
      trenner_anzeigen: true,
      icon_align_class: "is-vcentered",
      flex_direction_class: "is-flex-direction-row",
      row_direction_class: "is-row-normal-desktop",
      grid_columns: "",
      posts_per_page: 10,
    },
    settings: {
      time_control: "off",
      start_date: "",
      end_date: "",
      weekdays: [],
    },
  };

  return (
    <>
      <Section
        id={`event-facts-${id}`}
        columnsClassName={`is-multiline is-centered`}
      >
        <header className="column is-12-touch is-8-desktop">
          <h2 className={styles["event-facts-headline"]}>
            {getLabel("event_single_view_facts_headline")}
          </h2>
        </header>

        <ul
          className={`column is-12-touch is-8-desktop ${styles["event-info-boxes"]}`}
        >
          {/* Location */}
          <InfoBox
            title="Ort"
            icon="eventFactNobleBC"
            subline={
              <p>{`${name}, ${address_line_1}, ${postal_code} ${city}`}</p>
            }
          />
          {/* Duration */}
          <InfoBox
            title="Dauer"
            icon="eventFactNobleBC"
            subline={<p>{calculateFormatedDuration(starts_at, ends_at)}</p>}
          />
          {/* Date */}
          <InfoBox
            title="Datum"
            icon="eventFactNobleBC"
            subline={
              <p>{getFormatedDatetimeSpan(starts_at, ends_at, locale)}</p>
            }
          />
          {/* Price */}
          <InfoBox
            title="Kosten"
            icon="eventFactNobleBC"
            subline={getAllTicketOptions(data)}
          />

          <li className={styles["buy-now-button"]}>
            <Button
              type="primary"
              href={public_url}
              target="_blank"
              aria-label={getLabel("aria_label_buy_event_ticket_button")}
            >
              {getLabel("buy_event_ticket_button")}
            </Button>
          </li>
        </ul>
      </Section>

      <LinkListModule
        data={moreEventsData}
        linkListOptions={linkListOptions ?? ({} as LinkListOptionsType)}
        isNobleElements={false}
      />
    </>
  );
};

type InfoBoxProps = {
  title: string;
  subline: ReactNode;
  icon: string;
  extraClassName?: string;
  link?: {
    title: string;
    ariaLabel: string;
    url: string;
  };
};

const InfoBox = ({
  title,
  subline,
  icon,
  extraClassName,
  link,
}: InfoBoxProps) => {
  const { getIcon } = useGlobalContext();

  return (
    <li className={`${styles["info-box"]} ${extraClassName}`}>
      <header className={styles["info-header"]}>
        <CustomSVG
          src={getIcon(icon)}
          className={styles["info-svg"]}
          wrapper="svg"
          height="100%"
          width="100%"
          aria-hidden
        />

        <div className={styles["title-wrapper"]}>
          <h3 className={`${styles["title"]} h3`}>{title}</h3>
          {subline}
        </div>
      </header>

      {link && (
        <div className={styles["link-button"]}>
          <Button
            type="primary"
            href={link.url}
            target="_blank"
            aria-label={link.ariaLabel}
          >
            {link.title}
          </Button>
        </div>
      )}
    </li>
  );
};
